import { create } from 'zustand';

import { openFolder, type SyncFolder, type SyncFolderFile } from './folder';
import { ICloudAuthError, icloudEnabled, icloudFolder } from './icloud';
import { readStoredFolder, runSync, type SyncResult, type SyncTarget } from './sync';

/**
 * The one sync in flight, and what the last one found, per target.
 *
 * Every trigger — startup, a change, the window coming back into view, the
 * Sync now button, a menu item in the desktop app — goes through `syncNow`,
 * which runs at most one sync at a time. A trigger that arrives mid-sync asks
 * for one more round afterwards rather than starting a second, so a burst of
 * edits costs two syncs, not twenty.
 *
 * A device can sync to a folder and to iCloud at once (`sync.ts`,
 * `SyncTarget`). Each is run in turn and reports on its own; a failure in one
 * does not stop the other.
 */

export type SyncPhase =
  /** Not set up. */
  | 'off'
  | 'idle'
  | 'syncing'
  /** A browser folder whose permission lapsed, or an iCloud sign-in that expired. */
  | 'needs-permission'
  | 'error';

export interface TargetState {
  phase: SyncPhase;
  label?: string | undefined;
  lastSyncAt?: string | undefined;
  lastResult?: SyncResult | undefined;
  files: SyncFolderFile[];
  message?: string | undefined;
}

export type SyncState = Record<SyncTarget, TargetState>;

const OFF: TargetState = { phase: 'off', files: [] };

export const useSyncState = create<SyncState>(() => ({ folder: OFF, icloud: OFF }));

const RANK: Record<SyncPhase, number> = {
  off: 0,
  idle: 1,
  syncing: 2,
  error: 3,
  'needs-permission': 4,
};

/** The state that most needs saying, for the footer: an expired sign-in over an error over a sync in progress. */
export function overallState(state: SyncState): TargetState {
  return state.icloud.phase !== 'off' && RANK[state.icloud.phase] >= RANK[state.folder.phase]
    ? state.icloud
    : state.folder;
}

let running: Promise<void> | null = null;
let again = false;

/**
 * @param interactive  inside a click: allowed to ask the browser for folder
 *   access or send the pilot to sign in. Unattended triggers never prompt;
 *   they report `needs-permission`.
 * @param only  sync one target, e.g. from its own Sync now button.
 */
export function syncNow({
  interactive = false,
  only,
}: { interactive?: boolean; only?: SyncTarget } = {}): Promise<void> {
  if (running !== null) {
    again = true;
    return running;
  }
  again = false;
  running = (async () => {
    try {
      await syncOnce(interactive, only);
      while (takeAgain()) {
        await syncOnce(false, undefined);
      }
    } finally {
      running = null;
    }
  })();
  return running;
}

function takeAgain(): boolean {
  const requested = again;
  again = false;
  return requested;
}

async function openTarget(target: SyncTarget): Promise<SyncFolder | null> {
  if (target === 'icloud') {
    return (await icloudEnabled()) ? icloudFolder() : null;
  }
  const stored = await readStoredFolder();
  return stored === null ? null : openFolder(stored);
}

function update(target: SyncTarget, next: Partial<TargetState>): void {
  useSyncState.setState((state) => ({ [target]: { ...state[target], ...next } }));
}

/** @returns whether anything was merged in from this target. */
async function syncTarget(target: SyncTarget, interactive: boolean): Promise<boolean> {
  let label: string | undefined;
  try {
    const folder = await openTarget(target);
    if (folder === null) {
      useSyncState.setState({ [target]: OFF });
      return false;
    }
    label = folder.label;
    let permission = await folder.permission();
    if (permission !== 'granted' && interactive) {
      permission = (await folder.requestPermission()) ? 'granted' : 'denied';
    }
    if (permission !== 'granted') {
      update(target, { phase: 'needs-permission', label, message: undefined });
      return false;
    }
    update(target, { phase: 'syncing', label });
    const result = await runSync(folder, target);
    update(target, {
      phase: result.failed.length > 0 ? 'error' : 'idle',
      label,
      lastSyncAt: result.at,
      lastResult: result,
      files: result.files,
      message:
        result.failed.length > 0
          ? `Could not read ${result.failed.map((f) => f.name).join(', ')}.`
          : undefined,
    });
    return result.merged.length > 0;
  } catch (error) {
    const expired = error instanceof ICloudAuthError;
    update(target, {
      phase: expired ? 'needs-permission' : 'error',
      label,
      message: expired ? undefined : error instanceof Error ? error.message : 'Sync failed.',
    });
    return false;
  }
}

async function syncOnce(interactive: boolean, only: SyncTarget | undefined): Promise<void> {
  if (only !== undefined) {
    await syncTarget(only, interactive);
    return;
  }
  await syncTarget('folder', interactive);
  const mergedFromICloud = await syncTarget('icloud', interactive);
  // What came in from iCloud belongs in the folder too, and the folder was
  // written before it arrived. One more pass carries it across.
  if (mergedFromICloud && useSyncState.getState().folder.phase === 'idle') {
    await syncTarget('folder', false);
  }
}
